<script setup lang="ts">
import type { BlossomCarousel } from '@blossom-carousel/vue'
import type { EntryInterfaceTypeFragment, MediaFragment, NewsEntryFragment, StaffMemberThumbFragment } from '#graphql-operations'

interface NewsHeroProps {
  data: NewsEntryFragment
}

const props = defineProps<NewsHeroProps>()
const medias = computed(() => props.data.coverMedia as MediaFragment[])
const colorScheme = useColorScheme(() => props.data.colorScheme)
const authors = computed(() => props.data.newsAuthor as StaffMemberThumbFragment[])
const categories = computed(() => props.data.newsCategory as EntryInterfaceTypeFragment[])

// const alignmentClass = computed(() =>
//   [props.data.heading, monogram.value, props.data.tagline].filter(Boolean).length <= 1
//     ? 'justify-center'
//     : 'justify-between',
// )
</script>

<template>
  <Section class="newsHero" :class="colorScheme">
    <figure class="grid items-start lg:grid-cols-2">
      <BlossomCarousel class="carousel flex h-auto w-full lg:h-[calc(100svh-var(--header-height))] lg:w-full lg:top-$header-height lg:sticky">
        <div v-for="media in medias" :key="media.id" data-blossom-slide class="slide flex-shrink-0 w-full aspect-1 lg:size-full lg:aspect-auto">
          <CraftMedia class="size-full object-cover" :media :img-props="{ sizes: '1024px lg:50vw' }" />
        </div>
      </BlossomCarousel>
      <figcaption class="c-$color-scheme-foreground px-3.75 py-5.5 text-center bg-$color-scheme-background flex flex-col order-first lg:px-18.5 lg:py-7.5">
        <div class="mx-auto flex flex-1 flex-col gap-8 max-w-4xl justify-between">
          <Breadcrumbs :data="data" />
          <div class="space-y-5">
            <div class="flex gap-4 items-baseline justify-center">
              <!-- <Text :text="`${$t('updated')} ${data.dateUpdated}`" class="typo-sans-1 uppercase" /> -->
              <ul v-if="categories.length > 0" class="flex gap-1 lg:mb-3">
                <li v-for="category in categories" :key="category.id">
                  <Chip :label="category.title" />
                </li>
              </ul>
            </div>
            <h1 v-if="data.heading || data.title" class="richtext-simple typo-serif-4 lt-lg:[&>br]:hidden" v-html="data.heading || data.title" />
            <Text v-if="data.description" class="typo-serif-2 w-full lg:mx-auto lg:mt-8 lg:max-w-2xl" :html="data.description" />
            <Text v-else-if="data.excerpt" class="typo-serif-2 w-full lg:mx-auto lg:mt-8 lg:max-w-2xl" :html="data.excerpt" />
          </div>
          <template v-for="author in authors" :key="author!.id">
            <Anchor v-if="author" :to="author.url" rel="author" class="author mx-auto flex flex-col gap-4 w-fit items-center">
              <!-- <CraftMedia v-for="media in author.thumbMedia" :key="media!.id" class="rounded w-15 of-hidden" :media /> -->
              <div class="grid">
                <Text :text="author.title" class="typo-serif-2" />
                <Text :text="author.role" class="typo-sans-1 uppercase" />
              </div>
            </Anchor>
          </template>
          <div v-if="!authors.length" />
        </div>
      </figcaption>
    </figure>
  </Section>
</template>

<style scoped>
.newsHero figcaption {
  min-height: 50svh;

  @screen lg {
    min-height: calc(100svh - var(--header-height));
    height: 0;
  }
}

.newsHero .carousel {
  overflow: auto hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
}

.newsHero :deep(.breadcrumbs) {
  margin: 0;
}

.slide {
  scroll-snap-align: start;

  @screen lg {
    height: calc(100svh - var(--header-height));
  }
}
</style>
